import axios, { AxiosHeaders } from 'axios';

// ===== Utilidad para leer cookies (añadido) =====
function readCookie(name: string) {
  return document.cookie
    .split('; ')
    .find(r => r.startsWith(name + '='))
    ?.split('=')[1] || '';
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
});

// Interceptor: NUNCA Authorization. Añade CSRF solo en métodos mutadores.
apiClient.interceptors.request.use(
  (config) => {
    const method = (config.method || 'get').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrf = readCookie('csrf');
      if (!config.headers || !(config.headers instanceof AxiosHeaders)) {
        config.headers = new AxiosHeaders(config.headers as any);
      }
      (config.headers as AxiosHeaders).set('X-CSRF-Token', csrf);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

interface Degree {
  id: string;
  name: string;
  code: string;
  university_id: number;
  // Añade otras propiedades si es necesario
}

interface Subject {
  id: string; // ID único de la materia
  name: string; // Nombre de la materia
  code: string; // Código de la materia
  course: string; // Curso al que pertenece
  period: string; // Periodo (por ejemplo, SEMESTRE 2)
  type: string; // Tipo de materia (por ejemplo, OBLIGATORIAS)
  credits: number; // Créditos de la materia
  link: string; // Enlace a más información
  eval_type: string; // Tipo de evaluación (por ejemplo, Examen tipo test)
  degree_id: string; // ID del grado al que pertenece
  university_id: string; // ID del grado al que pertenece

}

interface Answer {
  is_correct: boolean;
  text: string;
  id: number;
  // Añade otras propiedades si es necesario
}

interface Exam {
  id: string;
  title: string;
  description: string;
  date: string | null; // Manejar el caso en que date sea null
  subject_id: string;
}

interface Question {
  id: string;
  text: string;
  context: string;
  isCodeContext: boolean;
  answers: Answer[];
  exams: Exam[]; // Cambiado de string[] a Exam[]
  reliability:string;
  // Añade otras propiedades si es necesario
}

interface ValidateAnswerResponse {
  message: string;
  answer_id: number;
  total_validations: number;
}

interface RegisterResponse {
  id: string;
  email: string;
  is_active: boolean;
  is_verified: boolean;
}

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  subjects: Subject[]; // Cambiado de any[] a Subject[]
}

interface UpdateUserProfileRequest {
  first_name?: string;
  last_name?: string;
  country_id?: number;
  user_type?: string;
  subject_ids?: string[]; // IDs de las materias asociadas
}

interface University {
  id: string;
  name: string;
  description: string;
  code: string;
  city_id: string;
  city: string;
  enabled: boolean;
  country: string;
}

interface Comment {
  id: number;
  text: string;
  rates: number[];
  average_rate: number;
}

interface RateCommentRequest {
  comment_id: number;
  value: number;
}

interface ChatConversationEntry {
  question: string;
  answer: string;
  response_id: string | null;
}


interface ChatQuestionContext {
  id: string;
  user_id: string;
  question_id: number;
  conversation: ChatConversationEntry[];
  created_at: string;
}

interface ChatContentContext {
  id: string;
  user_id: string;
  subject_content_id: number;
  conversation: ChatConversationEntry[];
  created_at: string;
}

interface SubjectContent {
  id: string;
  text: string;
  details: string;
}

/**
 * Recupera las universidades desde el endpoint /cities/:cityId/universities.
 * @param cityId ID de la ciudad para el parámetro de ruta.
 * @returns Lista de universidades con la estructura especificada.
 */
export const fetchUniversities = async (cityId: string): Promise<University[]> => {
  console.log("fetchUniversities");

  try {
    const response = await apiClient.get<University[]>(`/universities/?city_id=${cityId}`);
    console.log("Universidades recuperadas:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching universities:", error);
    throw error;
  }
};

export const fetchUniversityById = async (id: string): Promise<University> => {
  console.log("fetchUniversities");

  try {
    const response = await apiClient.get<University>(`/universities/${id}/`);
    console.log("Universidades recuperadas:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching universities:", error);
    throw error;
  }
};

export const fetchDegrees = async (universityID: string) => {
  console.log("fetchDegrees");

  try {
    console.log("baseURL: ", apiClient.defaults.baseURL);
    const response = await apiClient.get<Degree[]>(`/universities/${universityID}/degrees/`);
    console.log(response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching degrees:', error);
    throw error;
  }
};

export const fetchDegreeByID = async (universityID: string, degreeID: string) => {
  console.log("fetchDegreeByID");

  try {
    console.log("baseURL: ", apiClient.defaults.baseURL);
    const response = await apiClient.get<Degree>(`/universities/${universityID}/degrees/${degreeID}/`);
    console.log(response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching degrees:', error);
    throw error;
  }
};

export const fetchSubjects = async (university: string, degree: string) => {
  console.log("fetchSubjects");

  try {
    console.log("baseURL: ", apiClient.defaults.baseURL);
    const response = await apiClient.get<Subject[]>(`/universities/${university}/degrees/${degree}/subjects/`);
    console.log(response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};

export const fetchQuestions = async (university: string, degree: string, subjectID: string) => {
  console.log("fetchSubjects");

  try {
    console.log("baseURL: ", apiClient.defaults.baseURL);
    const response = await apiClient.get<Question[]>(`/universities/${university}/degrees/${degree}/subjects/${subjectID}/questions/random/`);
    console.log(response.data);

    return response.data;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};

// Login adaptado a cookies + CSRF
export const login = async (username: string, password: string): Promise<void> => {
  try {
    const data = new URLSearchParams({
      grant_type: 'password',
      username,
      password,
    }).toString();

    const res = await apiClient.post('/auth/cookie/login', data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    // Aceptar cualquier 2xx (el backend devuelve 204)
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`Login failed (status ${res.status})`);
    }

    // Intentar obtener cookie CSRF (ignorar fallo si ya existe)
    try {
      await apiClient.get('/auth/csrf');
    } catch (e) {
      console.warn('No se pudo obtener CSRF (puede que ya exista):', e);
    }
  } catch (error) {
    console.error('Error during login (cookie mode):', error);
    throw error;
  }
};

// Logout adaptado
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/cookie/logout');
  } catch (error) {
    console.error("Error during logout:", error);
    throw error;
  }
};

/**
 * Recupera las ciudades desde el endpoint /cities.
 * Pasa el countryId como un parámetro de consulta.
 * @param countryId ID del país para el parámetro de consulta.
 * @returns Lista de ciudades.
 */
export const fetchCities = async (countryId: string) => {
  console.log("fetchCities");

  try {
    const response = await apiClient.get(`/cities?country_id=${countryId}`);
    console.log("Ciudades recuperadas:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching cities:", error);
    throw error;
  }
};

/**
 * Recupera un subject específico por su ID.
 * @param universityID ID de la universidad.
 * @param degreeID ID del grado.
 * @param subjectID ID del subject.
 * @returns Información del subject.
 */
export const fetchSubjectById = async (universityID: string, degreeID: string, subjectID: string): Promise<Subject> => {
  try {
    const response = await apiClient.get<Subject>(`/universities/${universityID}/degrees/${degreeID}/subjects/${subjectID}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subject by ID:", error);
    throw error;
  }
};

/**
 * Recupera los comentarios de una pregunta específica.
 * @param universityID ID de la universidad.
 * @param degreeID ID del grado.
 * @param subjectID ID de la asignatura.
 * @param questionID ID de la pregunta.
 * @returns Lista de comentarios.
 */
export const fetchComments = async (
  universityID: string,
  degreeID: string,
  subjectID: string,
  questionID: string
): Promise<Comment[]> => {
  try {
    const response = await apiClient.get<Comment[]>(
      `/universities/${universityID}/degrees/${degreeID}/subjects/${subjectID}/questions/${questionID}/comments/`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching comments:", error);
    throw error;
  }
};

/**
 * Recupera los exámenes de una asignatura específica.
 * @param universityID ID de la universidad.
 * @param degreeID ID del grado.
 * @param subjectID ID de la asignatura.
 * @returns Lista de exámenes.
 */
export const fetchExams = async (
  universityID: string,
  degreeID: string,
  subjectID: string
): Promise<Exam[]> => {
  try {
    const response = await apiClient.get<Exam[]>(
      `/universities/${universityID}/degrees/${degreeID}/subjects/${subjectID}/exams/`
    );
    console.log("Exámenes recuperados:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching exams:", error);
    throw error;
  }
};

/**
 * Recupera las preguntas de un examen específico.
 * @param universityID ID de la universidad.
 * @param degreeID ID del grado.
 * @param subjectID ID de la asignatura.
 * @param examID ID del examen.
 * @returns Lista de preguntas.
 */
export const fetchExamQuestions = async (
  universityID: string,
  degreeID: string,
  subjectID: string,
  examID: string
): Promise<Question[]> => {
  try {
    const response = await apiClient.get<Question[]>(
      `/universities/${universityID}/degrees/${degreeID}/subjects/${subjectID}/exams/${examID}/questions/`
    );
    console.log("Preguntas del examen recuperadas:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching exam questions:", error);
    throw error;
  }
};



/**
 * Recupera los contenidos de una asignatura desde el endpoint /subjects/{subject_id}/contents/.
 * @param subjectId ID de la asignatura.
 * @returns Lista de contenidos con id y texto.
 */
export const fetchSubjectContents = async (subjectId: string): Promise<SubjectContent[]> => {
  try {
    const response = await apiClient.get<SubjectContent[]>(`/subjects/${subjectId}/contents/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subject contents:", error);
    throw error;
  }
};

export const getUserProfile = async (): Promise<UserProfile> => {
  try {
    const response = await apiClient.get<UserProfile>("/users/me");
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// Exportar la interfaz Degree
export type { Degree, Subject, Question, ValidateAnswerResponse, RegisterResponse, Exam, UserProfile, UpdateUserProfileRequest, University, Comment, RateCommentRequest, ChatConversationEntry, ChatQuestionContext, SubjectContent };

/**
 * Recupera las tareas abiertas desde el endpoint /tasks/open.
 * @returns Lista de tareas abiertas con la estructura especificada.
 */
export const fetchOpenTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/tasks/search/status?status=Open&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const open_tasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
    }));
    return open_tasks;
  } catch (error) {
    console.error("Error fetching open tasks:", error);
    throw error;
  }
};


// Recupera los detalles de una tarea específica
export const getTaskDetails = async (taskId: string): Promise<{
  task_id: string;
  name: string;
  description: string;
  state: string;
  subject_name: string;
  subject_id: number;
  subject_code: string;
  user_id: string;
  university_name: string;
  degree_name: string;
  university_id: number;
  degree_id: number;
  biblio_subjects: string[];
  documents: { name: string; url: string }[]; // Formatear documentos
  exams: { name: string; url: string }[]; // Formatear exámenes
  eval_type: string;
  degree_code: string;
  type_subject: string;
}> => {
  try {
    const response = await apiClient.get(`/tasks/${taskId}`);
    return {
      task_id: response.data.id,
      name: response.data.name,
      description: response.data.description,
      state: response.data.state,
      subject_name: response.data.subject_name,
      subject_id: response.data.subject_id,
      user_id: response.data.user_id,
      university_name: response.data.university_name,
      university_id: response.data.university_id,
      degree_id: response.data.degree_id,
      degree_name: response.data.degree_name,
      biblio_subjects: response.data.biblio_subjects,
      documents: response.data.documents.map((doc: string) => ({
        name: doc.split("/").pop() || "Documento",
        url: doc,
      })),
      exams: response.data.exams.map((exam: string) => ({
        name: exam.split("/").pop() || "Examen",
        url: exam,
      })),
      eval_type: response.data.eval_type,
      degree_code: response.data.degree_code || "Unknown Code",
      subject_code: response.data.subject_code || "Unknown Code",
      type_subject: response.data.type_subject || "Unknown Type",
    };
  } catch (error) {
    console.error("Error fetching task details:", error);
    throw error;
  }
};

// Sube documentos relacionados con una tarea
export const uploadTaskDocuments = async (
  taskId: string,
  files: File[]
): Promise<any> => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await apiClient.post(`/tasks/${taskId}/upload-documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading task documents:", error);
    throw error;
  }
};

// Sube exámenes relacionados con una tarea
export const uploadTaskExams = async (
  taskId: string,
  files: File[]
): Promise<any> => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await apiClient.post(`/tasks/${taskId}/upload-exams`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading task exams:", error);
    throw error;
  }
};

// Completa una tarea
export const completeTask = async (taskId: string, evalType: string, no_exams: boolean): Promise<any> => {
  try {
    const params = new URLSearchParams();
    if (evalType) {
      params.append("eval_type", evalType); // Agrega evalType si no está vacío
    }
    params.append("no_exams", no_exams.toString()); // Agrega no_exams como string

    const response = await apiClient.post(`/tasks/${taskId}/complete?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error completing task:", error);
    throw error;
  }
};

// Libera una tarea
export const releaseTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/tasks/${taskId}/release`);
    return response.data;
  } catch (error) {
    console.error("Error releasing task:", error);
    throw error;
  }
};

// Reclama una tarea
export const claimTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/tasks/${taskId}/claim`);
    return response.data;
  } catch (error) {
    console.error("Error claiming task:", error);
    throw error;
  }
};

export const fetchMyTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/tasks/search/status?status=Working&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const myTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return myTasks;
  } catch (error) {
    console.error("Error fetching my tasks:", error);
    throw error;
  }
};

export const deleteTaskItem = async (
  taskId: string,
  type: "document" | "exam",
  url: string
): Promise<void> => {
  try {
    const response = await apiClient.post(`/tasks/${taskId}/delete-item`, {
      type,
      url,
    });
    console.log("Item eliminado:", response.data);
  } catch (error) {
    console.error("Error deleting task item:", error);
    throw error;
  }
};

/**
 * Recupera las tareas con errores desde el endpoint /tasks/status/search.
 * @returns Lista de tareas con errores.
 */
export const fetchErrorTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/tasks/search/status?status=Error&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const errorTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
      workflow_ref: task.workflow_ref || "Unknown Workflow Reference",
      eval_type: task.eval_type || "Unknown Evaluation Type",
    }));
    return errorTasks;
  } catch (error) {
    console.error("Error fetching error tasks:", error);
    throw error;
  }
};

/**
 * Recupera las tareas cerradas con estado KO desde el endpoint /tasks/status/search.
 * @returns Lista de tareas cerradas con estado KO.
 */
export const fetchClosedKOTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/tasks/search/status?status=ClosedKO&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const closedKOTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return closedKOTasks;
  } catch (error) {
    console.error("Error fetching closed KO tasks:", error);
    throw error;
  }
};

interface CompletedTasksSummary {
  summary: {
    user_id: string;
    email: string;
    completed_tasks: number;
  }[];
}

/**
 * Recupera el resumen de tareas completadas por usuario entre dos fechas.
 * @param startDate Fecha de inicio en formato ISO 8601.
 * @param endDate Fecha de fin en formato ISO 8601.
 * @param state Estado de las tareas (por defecto "CLOSED").
 * @returns Lista de usuarios con el número de tareas completadas.
 */
export const fetchCompletedTasksSummary = async (
  startDate: string,
  endDate: string,
  state: string = "CLOSED"
): Promise<CompletedTasksSummary> => {
  try {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      state,
    });

    const response = await apiClient.get<CompletedTasksSummary>(
      `/tasks/search/summary?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching completed tasks summary:", error);
    throw error;
  }
};

/**
 * Actualiza el type_subject de una asignatura.
 * @param universityId ID de la universidad.
 * @param degreeId ID del grado.
 * @param subjectId ID de la asignatura.
 * @param typeSubject Nuevo valor de type_subject.
 * @returns La respuesta del backend (SubjectResponse).
 */
export const updateTaskTypeSubject = async (
  universityId: number,
  degreeId: number,
  subjectId: number,
  typeSubject: string
): Promise<any> => {
  try {
    const response = await apiClient.patch(
      `/universities/${universityId}/degrees/${degreeId}/subjects/${subjectId}/type-subject?type_subject=${encodeURIComponent(typeSubject)}`
      // No enviar body, solo query param
    );
    return response.data;
  } catch (error) {
    console.error("Error updating type_subject:", error);
    throw error;
  }
};

/**
 * Descarta una tarea específica.
 * @param taskId ID de la tarea a descartar.
 * @returns Respuesta del backend.
 */
export const discardTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/tasks/${taskId}/discard`);
    return response.data;
  } catch (error) {
    console.error("Error discarding task:", error);
    throw error;
  }
};

/**
 * Recupera las tareas descartadas desde el endpoint /tasks/search/status?status=Discarted.
 * @returns Lista de tareas descartadas.
 */
export const fetchDiscartedTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/tasks/search/status?status=Discarted&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const discartedTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return discartedTasks;
  } catch (error) {
    console.error("Error fetching discarted tasks:", error);
    throw error;
  }
};

/**
 * Descarga un archivo asociado a una tarea desde Google Cloud Storage (privado).
 * @param taskId ID de la tarea.
 * @param path Ruta relativa dentro del bucket (por ejemplo: UNED/6502/69904034/exams/E699040340A23F1.pdf).
 * @returns Blob del archivo descargado.
 */
export const downloadTaskFileFromGCS = async (
  taskId: string | number,
  path: string
): Promise<Blob> => {
  try {
    // Es correcto usar responseType: "blob" para obtener archivos binarios (PDF, imágenes, etc.)
    const response = await apiClient.get(
      `/tasks/${taskId}/download-from-gcs`,
      {
        params: { path },
        responseType: "blob", // Esto es necesario para visor PDF/imágenes
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error downloading file from GCS:", error);
    throw error;
  }
};

// ========== EXAM TASKS ENDPOINTS (equivalentes a /tasks/, pero con /exam-tasks/) ==========

// Lista tareas abiertas de exámenes
export const fetchOpenExamTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/exam-tasks/search/status?status=Open&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const open_tasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
    }));
    return open_tasks;
  } catch (error) {
    console.error("Error fetching open exam tasks:", error);
    throw error;
  }
};

// Recupera los detalles de una tarea de examen específica
export const getExamTaskDetails = async (taskId: string): Promise<{
  task_id: string;
  name: string;
  description: string;
  state: string;
  subject_name: string;
  subject_id: number;
  subject_code: string;
  user_id: string;
  created_by_id: string;
  university_name: string;
  degree_name: string;
  university_id: number;
  degree_id: number;
  exam: string;
  eval_type: string;
  degree_code: string;
  type_subject: string;
  current_exams: string[];
}> => {
  try {
    const response = await apiClient.get(`/exam-tasks/${taskId}`);
    return {
      task_id: response.data.id,
      name: response.data.name,
      description: response.data.description,
      state: response.data.state,
      subject_name: response.data.subject_name,
      subject_id: response.data.subject_id,
      user_id: response.data.user_id,
      created_by_id: response.data.created_by_id,
      university_name: response.data.university_name,
      university_id: response.data.university_id,
      degree_id: response.data.degree_id,
      degree_name: response.data.degree_name,
      exam: response.data.exam,
      eval_type: response.data.eval_type,
      degree_code: response.data.degree_code || "Unknown Code",
      subject_code: response.data.subject_code || "Unknown Code",
      type_subject: response.data.type_subject || "Unknown Type",
      current_exams: response.data.current_exams || [], // Asegúrate de que current_exams sea un array
    };
  } catch (error) {
    console.error("Error fetching exam task details:", error);
    throw error;
  }
};

// Sube documentos relacionados con una tarea de examen
export const uploadExamTaskDocuments = async (
  taskId: string,
  files: File[]
): Promise<any> => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await apiClient.post(`/exam-tasks/${taskId}/upload-documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading exam task documents:", error);
    throw error;
  }
};

// Sube exámenes relacionados con una tarea de examen
export const uploadExamTaskExams = async (
  taskId: string,
  files: File[]
): Promise<any> => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await apiClient.post(`/exam-tasks/${taskId}/upload-exams`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading exam task exams:", error);
    throw error;
  }
};

// Completa una tarea de examen
export const completeExamTask = async (taskId: string, evalType: string): Promise<any> => {
  try {
    const params = new URLSearchParams();
    if (evalType) {
      params.append("eval_type", evalType);
    }
    const response = await apiClient.post(`/exam-tasks/${taskId}/complete?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error("Error completing exam task:", error);
    throw error;
  }
};

// Libera una tarea de examen
export const releaseExamTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/exam-tasks/${taskId}/release`);
    return response.data;
  } catch (error) {
    console.error("Error releasing exam task:", error);
    throw error;
  }
};

// Reclama una tarea de examen
export const claimExamTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/exam-tasks/${taskId}/claim`);
    return response.data;
  } catch (error) {
    console.error("Error claiming exam task:", error);
    throw error;
  }
};

// Lista mis tareas de examen
export const fetchMyExamTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/exam-tasks/search/status?status=Working&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const myTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return myTasks;
  } catch (error) {
    console.error("Error fetching my exam tasks:", error);
    throw error;
  }
};

// Elimina un documento o examen de una tarea de examen
export const deleteExamTaskItem = async (
  taskId: string,
  type: "document" | "exam",
  url: string
): Promise<void> => {
  try {
    const response = await apiClient.post(`/exam-tasks/${taskId}/delete-item`, {
      type,
      url,
    });
    console.log("Item eliminado:", response.data);
  } catch (error) {
    console.error("Error deleting exam task item:", error);
    throw error;
  }
};

// Lista tareas de examen con errores
export const fetchErrorExamTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/exam-tasks/search/status?status=Error&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const errorTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
      workflow_ref: task.workflow_ref || "Unknown Workflow Reference",
      eval_type: task.eval_type || "Unknown Evaluation Type",
    }));
    return errorTasks;
  } catch (error) {
    console.error("Error fetching error exam tasks:", error);
    throw error;
  }
};

// Lista tareas de examen cerradas con estado KO
export const fetchClosedKOExamTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/exam-tasks/search/status?status=ClosedKO&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const closedKOTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return closedKOTasks;
  } catch (error) {
    console.error("Error fetching closed KO exam tasks:", error);
    throw error;
  }
};

// Resumen de tareas de examen completadas por usuario
interface CompletedExamTasksSummary {
  summary: {
    user_id: string;
    email: string;
    completed_tasks: number;
  }[];
}

export const fetchCompletedExamTasksSummary = async (
  startDate: string,
  endDate: string,
  state: string = "CLOSED"
): Promise<CompletedExamTasksSummary> => {
  try {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      state,
    });

    const response = await apiClient.get<CompletedExamTasksSummary>(
      `/exam-tasks/search/summary?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching completed exam tasks summary:", error);
    throw error;
  }
};

// Descarta una tarea de examen
export const discardExamTask = async (taskId: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/exam-tasks/${taskId}/discard`);
    return response.data;
  } catch (error) {
    console.error("Error discarding exam task:", error);
    throw error;
  }
};

// Lista tareas de examen descartadas
export const fetchDiscartedExamTasks = async (): Promise<
  {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state: string;
  }[]
> => {
  try {
    const response = await apiClient.get(`/exam-tasks/search/status?status=Discarted&offset=0&limit=20`);
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Invalid response format");
    }
    const discartedTasks = response.data.map((task: any) => ({
      task_id: task.task_id,
      name: task.name,
      description: task.description,
      subject_name: task.subject_name || "Unknown Subject",
      university_name: task.university_name || "Unknown University",
      degree_name: task.degree_name || "Unknown Degree",
      state: task.state || "Unknown State",
    }));
    return discartedTasks;
  } catch (error) {
    console.error("Error fetching discarted exam tasks:", error);
    throw error;
  }
};

// Descarga un archivo asociado a una tarea de examen desde Google Cloud Storage (privado)
export const downloadExamTaskFileFromGCS = async (
  taskId: string | number,
  path: string
): Promise<Blob> => {
  try {
    const response = await apiClient.get(
      `/exam-tasks/${taskId}/download-from-gcs`,
      {
        params: { path },
        responseType: "blob",
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error downloading exam task file from GCS:", error);
    throw error;
  }
};